import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Grid, Tab, Tabs } from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { resumoService } from '@/services/resumoService'
import { PendenciasResumo } from '@/types/resumos'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import { pendenciaService } from '@/services/pendenciaService'
import type { CentralPendencias } from '@/types/pendencia'
import MinhasPendenciasPage from './MinhasPendenciasPage'
import CentralPendenciasLista from './CentralPendenciasLista'
import DocumentosListPage from '@/pages/documentos/DocumentosListPage'

/**
 * Pendências e Documentos (Figma 26:202, Onda 2 / A04) — página única com abas.
 *
 * KPIs de `GET /v1/pendencias/resumo` (A03): Em aberto / Bloqueantes / Em análise /
 * Concluídas — a MESMA fonte da inbox listada na aba de pendências. "Bloqueante" é a
 * pendência cujo documento solicitado é item obrigatório do checklist (V27).
 *
 * O `?aba=documentos` dos redirects do A02 (`/documentos`) continua honrado aqui.
 *
 * VC-09 (Onda 1 da Visão do Consultor): para o consultor o título é "Central de
 * Pendências"; o back-office mantém "Pendências e Documentos". Mesma rota.
 *
 * VC-09 parte 2 (Onda 2 / DEV-4): para o consultor, a aba "Pendências" é a Central
 * agregada (`GET /v1/pendencias/central` — pendências, documentos faltando, NF a emitir,
 * próximas ações e reuniões). A MESMA resposta traz `{ itens, resumo }`: os KPIs do topo
 * são o `resumo`, contagem da lista no mesmo cálculo — uma chamada só (correção VAL-R).
 * O back-office segue inalterado (inbox + `/v1/pendencias/resumo`).
 */
const PendenciasDocumentosPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const consultor = ehConsultor(authStore((s) => s.usuario)?.permissoes)
  const aba = searchParams.get('aba') === 'documentos' ? 'documentos' : 'pendencias'

  const [resumo, setResumo] = useState<PendenciasResumo | null>(null)
  const [central, setCentral] = useState<CentralPendencias | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [estadoErro, setEstadoErro] = useState<'erro' | 'semPermissao'>('erro')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      if (consultor) setCentral(await pendenciaService.central())
      else setResumo(await resumoService.pendencias())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o resumo')
      setEstadoErro((e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [consultor])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const trocarAba = (nova: string) => {
    if (nova === 'pendencias') searchParams.delete('aba')
    else searchParams.set('aba', nova)
    setSearchParams(searchParams, { replace: true })
  }

  return (
    <Box>
      <PageHeader
        title={consultor ? 'Central de Pendências' : 'Pendências e Documentos'}
        subtitle={
          consultor
            ? 'Tudo o que você precisa fazer: pendências, documentos, NF, próximas ações e reuniões'
            : 'O que a Originei pediu e os documentos das suas reservas'
        }
      />

      {erro && consultor ? null : erro ? (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'erro' && {
            mensagem: 'Não foi possível carregar os indicadores de pendências',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
          minHeight={160}
        />
      ) : consultor ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/central → resumo.total — contagem da mesma lista abaixo */}
            <KpiCard
              label="O que fazer"
              valor={central?.resumo.total ?? '—'}
              legenda="Itens na sua Central"
              variante="escura"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/central → resumo.bloqueantes */}
            <KpiCard
              label="Bloqueantes"
              valor={central?.resumo.bloqueantes ?? '—'}
              legenda="Travam o andamento da reserva"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/central → resumo.pendencias */}
            <KpiCard
              label="Pendências"
              valor={central?.resumo.pendencias ?? '—'}
              legenda="Pedidas pela Originei"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/central → resumo.documentosFaltando */}
            <KpiCard
              label="Documentos faltando"
              valor={central?.resumo.documentosFaltando ?? '—'}
              legenda="Obrigatórios do checklist"
              carregando={carregando}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/resumo (emAberto) */}
            <KpiCard
              label="Em aberto"
              valor={resumo?.emAberto ?? '—'}
              legenda="Aguardando sua resposta"
              variante="escura"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/resumo (bloqueantes — item do checklist V27) */}
            <KpiCard
              label="Bloqueantes"
              valor={resumo?.bloqueantes ?? '—'}
              legenda="Travam o envio da reserva"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/resumo (emAnalise) */}
            <KpiCard
              label="Em análise"
              valor={resumo?.emAnalise ?? '—'}
              legenda="Na mão da Originei"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/pendencias/resumo (concluidas) */}
            <KpiCard
              label="Concluídas"
              valor={resumo?.concluidas ?? '—'}
              carregando={carregando}
            />
          </Grid>
        </Grid>
      )}

      <Tabs value={aba} onChange={(_, v) => trocarAba(v)} sx={{ mb: 2 }}>
        <Tab label="Pendências" value="pendencias" />
        <Tab label="Documentos" value="documentos" />
      </Tabs>

      {aba === 'documentos' ? (
        <DocumentosListPage embutida />
      ) : consultor ? (
        // Erro de carga da Central aparece uma vez só, aqui, com "Tentar novamente".
        <CentralPendenciasLista
          itens={central?.itens ?? []}
          resumo={central?.resumo}
          carregando={carregando}
          erro={erro}
          semPermissao={erro !== null && estadoErro === 'semPermissao'}
          onTentarNovamente={() => void carregar()}
        />
      ) : (
        <MinhasPendenciasPage embutida />
      )}
    </Box>
  )
}

export default PendenciasDocumentosPage
